/**
 * Where a transfer plan's QR code points.
 *
 * Pure and separate from the component that draws it, because the rule below
 * is the part that is easy to get wrong and impossible to notice: a QR built
 * the obvious way works on the machine that made it and fails on every handset.
 *
 * IT MUST BE A `/m/` PATH. A phone loading any desktop route is redirected to
 * `/m/index.html` by branch (b) of the inline script in `index.html`, and that
 * branch does NOT stash the path — the route is discarded and the tech lands
 * on the mobile home screen, one tap from nothing. A `/m/` path is caught by
 * branch (a) instead, which remembers it and lets
 * `src/mobile/lib/deepLinkBridge.ts` replay it once the mobile app is running.
 *
 * That bridge exists precisely because CloudFront's `default_root_object`
 * applies only at the distribution root, so `/m/` and `/m/*` are missing S3
 * keys. It costs one HTML round trip on the way through and needs no new
 * mechanism — which is why this encodes the plain deep path rather than trying
 * to hand-roll an entry URL with the route in a query nothing reads.
 */
export function transferPlanUrl(origin: string, transferId: string): string {
  const id = transferId?.trim() ?? ''
  if (!id) throw new Error('Cannot build a plan QR without a transfer id')

  const base = origin?.trim().replace(/\/+$/, '') ?? ''
  if (!base) throw new Error('Cannot build a plan QR without an origin')

  return `${base}/m/storage-transfers/${encodeURIComponent(id)}`
}

/**
 * Where ONE move's QR code points: that move on the handset, so a tech at the
 * freezer lands on exactly the item they are holding rather than a list of a
 * thousand. Built on `transferPlanUrl` so it inherits the same `/m/` rule.
 */
export function transferMoveUrl(origin: string, transferId: string, moveId: string): string {
  const move = moveId?.trim() ?? ''
  if (!move) throw new Error('Cannot build a move QR without a move id')
  return `${transferPlanUrl(origin, transferId)}/moves/${encodeURIComponent(move)}`
}
